import clsx from 'clsx'
import { FilePdf, Document, AlertErrorCircle, XCircle } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { MedChronFileStatus } from '@/medical-chronology/jobs/components/files/MedChronFileCard'
import type { DispositionDisplay } from './disposition-display'

/**
 * A staged document row on the Add Files surface (Figma "upload-card"): a PDF icon, the
 * filename, and the file's disposition against the living chronology as the status line.
 * Distinct from the shared jobs MedChronFileCard, which renders upload/extraction progress.
 *
 * A file is staged the moment init-upload returns a user_file_id — before the Firebase upload
 * and server processing have necessarily finished. While that pipeline is still running
 * (status UPLOADING/PROCESSING) the row mirrors MedChronFileCard: a spinner in the left
 * section plus an italic "Uploading…"/"Processing…" line, so the row reflects real progress
 * instead of sitting static until the server preview disposition lands. Error and disposition
 * states are unchanged and take precedence over (error) / yield to (disposition) the progress line.
 */
export function StagedFileCard({
  fileName,
  status,
  disposition,
  duplicateOf,
  errorMessage,
  onRemove,
  onOpen,
}: {
  fileName: string
  status?: MedChronFileStatus
  disposition: DispositionDisplay | null
  duplicateOf?: string
  errorMessage?: string
  onRemove: () => void
  onOpen?: () => void
}) {
  const FileIcon = fileName.toLowerCase().endsWith('.pdf') ? FilePdf : Document
  const Icon = disposition?.Icon
  const clickable = Boolean(onOpen)
  // The upload pipeline is still running and nothing has errored: show progress like MedChronFileCard.
  const inProgress = !errorMessage && (status === MedChronFileStatus.UPLOADING || status === MedChronFileStatus.PROCESSING)
  return (
    <div
      className={clsx(
        'h-[60px] flex items-center gap-[10px] rounded-[5px] border border-brand-neutral-200 bg-white relative',
        clickable && 'cursor-pointer hover:border-brand-neutral-300'
      )}
      onClick={onOpen}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpen?.()
              }
            }
          : undefined
      }
    >
      <div className="w-[60px] h-full flex items-center justify-center px-3 border-r rounded-l-[5px]">
        {inProgress ? <CircularProgressContinuousSized size={24} thickness={3} showTrack={true} /> : <FileIcon className="w-6 h-6 text-brand-neutral-800" />}
      </div>
      <div className="flex-1 flex flex-col gap-1 justify-center pr-12 min-w-0">
        <p className="text-[13px] font-medium text-brand-neutral-950 truncate">{fileName}</p>
        {errorMessage ? (
          <div className="flex items-center gap-1 text-red-800">
            <AlertErrorCircle className="w-3.5 h-3.5" />
            <p className="text-xs font-medium">{errorMessage}</p>
          </div>
        ) : inProgress ? (
          <p className="text-xs font-medium italic text-brand-neutral-600">{status === MedChronFileStatus.UPLOADING ? 'Uploading...' : 'Processing...'}</p>
        ) : (
          disposition &&
          Icon && (
            <div className={clsx('flex items-center gap-1', disposition.className)}>
              {disposition.loading ? (
                <span className="w-3.5 h-3.5 shrink-0 flex items-center justify-center">
                  <CircularProgressContinuousSized size={12} thickness={4} />
                </span>
              ) : (
                <Icon className="w-3.5 h-3.5 shrink-0" />
              )}
              <p className="text-xs font-medium">{disposition.text}</p>
            </div>
          )
        )}
        {duplicateOf && <p className="text-xs font-medium text-yellow-700">Possible duplicate of {duplicateOf}</p>}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-brand-neutral-50 transition-colors duration-200"
        aria-label={`Remove ${fileName}`}
      >
        <XCircle className="w-5 h-5 text-brand-neutral-600 hover:text-brand-neutral-800" />
      </button>
    </div>
  )
}
